import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Administrator } from '../../models/administrator.model';
import { DataFormModel } from '../../models/data-form-model.model';
import { AdministratorService } from '../../services/administrator.service';
import { UtilService } from '../../services/util.service';

/**
 * Para qué se abrió el modal.
 *
 * - `administrator`: la pantalla de sudo configura a OTRO administrador. Se ven
 *   los tres switches y se escribe sobre `/administrators/{uid}`.
 * - `preferences`: el administrador configura lo suyo desde el menú de su
 *   cuenta. Solo se ve "Notificar reportes" y se escribe sobre
 *   `/administrators/preferences`.
 *
 * Un solo input decide las tres cosas que cambian entre un caso y otro —campos
 * visibles, título y endpoint— en vez de tres banderas sueltas que puedan
 * contradecirse. El valor por defecto es el uso que ya existía, así que
 * `administrators-page` no se entera de nada.
 */
export type AdministratorModalMode = 'administrator' | 'preferences';

@Component({
  selector: 'app-administrator-modal',
  templateUrl: './administrator-modal.component.html',
  styleUrl: './administrator-modal.component.scss'
})
export class AdministratorModalComponent {

  @Input() administrator?: Administrator;
  @Input() neighborhoodId?: string;
  @Input() mode: AdministratorModalMode = 'administrator';

  public dataForm: DataFormModel = {
    isOwner: {
      value: false,
      type: 'switch',
      label: 'Dueño del fraccionamiento',
      required: false,
      validators: []
    },
    paymentsNotification: {
      value: true,
      type: 'switch',
      label: 'Notificar pagos',
      required: false,
      validators: []
    },
    reportsNotification: {
      value: true,
      type: 'switch',
      label: 'Notificar reportes',
      required: false,
      validators: []
    }
  };

  /** Campos que el modo deja ver. El `dataForm` sigue teniendo los tres: el
      modo decide cuáles se construyen y se pintan, y así `formAdministrator.value`
      lleva exactamente lo que el endpoint de ese modo espera. */
  private readonly fieldsByMode: Record<AdministratorModalMode, string[]> = {
    administrator: ['isOwner', 'paymentsNotification', 'reportsNotification'],
    preferences: ['reportsNotification']
  };

  public visibleKeys: string[] = [];

  public submitted: boolean = false;
  public loading: boolean = false;

  /** La lectura del registro propio falló. Sin él no hay nada que editar: se
      dice y se ofrece reintentar, en vez de pintar switches inventados. */
  public loadFailed: boolean = false;

  public formAdministrator!: FormGroup;

  constructor(
    public activeModal: NgbActiveModal,
    public administratorService: AdministratorService,
    public utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  get f() {
    return this.formAdministrator.controls;
  }

  /** Título del modal. En preferencias no aplica el "Configuración de …": el
      administrador se está configurando a sí mismo. */
  get title(): string {
    return this.mode === 'preferences'
      ? 'Preferencias'
      : `Configuración de ${this.administrator?.fullName ?? ''}`;
  }

  /*
  ██╗      ██████╗  █████╗ ██████╗ ██████╗  █████╗ ████████╗ █████╗
  ██║     ██╔═══██╗██╔══██╗██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
  ██║     ██║   ██║███████║██║  ██║██║  ██║███████║   ██║   ███████║
  ██║     ██║   ██║██╔══██║██║  ██║██║  ██║██╔══██║   ██║   ██╔══██║
  ███████╗╚██████╔╝██║  ██║██████╔╝██████╔╝██║  ██║   ██║   ██║  ██║
  ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

  */
  // region loadData
  async loadData() {

    this.loading = true;
    this.loadFailed = false;

    /* Se resuelven antes del await: el esqueleto pinta una fila por campo
       visible y así el modal no cambia de alto cuando llega el dato. */
    this.visibleKeys = this.fieldsByMode[this.mode];

    /* En preferencias nadie pudo pasarnos el registro: el listado de
       administradores es solo para sudo. El modal pide el suyo y mientras
       tanto el cuerpo muestra el esqueleto de la fila. */
    if (this.mode === 'preferences') {

      this.administrator = await this.administratorService.getPreferences(
        this.neighborhoodId!
      );

      this.loadFailed = !this.administrator;

    }

    if (this.administrator) {
      this.dataForm['isOwner'].value = this.administrator.isOwner;
      this.dataForm['paymentsNotification'].value = this.administrator.paymentsNotification;
      this.dataForm['reportsNotification'].value = this.administrator.reportsNotification;
    }

    this.formAdministrator = this.utilService.buildForm(
      this.buildFormModel()
    );

    this.loading = false;

  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR buildFormModel                           */
  /* -------------------------------------------------------------------------- */
  // region buildFormModel
  /** Subconjunto del dataForm que corresponde al modo. */
  private buildFormModel(): DataFormModel {

    return this.visibleKeys.reduce<DataFormModel>(
      (model, key) => {
        model[key] = this.dataForm[key];
        return model;
      },
      {}
    );

  }

  /*
   ██████╗ ███╗   ██╗███████╗██╗   ██╗██████╗ ███╗   ███╗██╗████████╗
  ██╔═══██╗████╗  ██║██╔════╝██║   ██║██╔══██╗████╗ ████║██║╚══██╔══╝
  ██║   ██║██╔██╗ ██║███████╗██║   ██║██████╔╝██╔████╔██║██║   ██║
  ██║   ██║██║╚██╗██║╚════██║██║   ██║██╔══██╗██║╚██╔╝██║██║   ██║
  ╚██████╔╝██║ ╚████║███████║╚██████╔╝██████╔╝██║ ╚═╝ ██║██║   ██║
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝   ╚═╝

  */
  // region onSubmit
  async onSubmit() {

    this.submitted = true;

    if (!this.formAdministrator || this.formAdministrator.invalid) {
      return;
    }

    await this.update();
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  // region update
  async update() {

    /* Cada modo escribe en su propio endpoint: el de sudo apunta a un uid
       ajeno, el de preferencias no lleva uid porque el backend resuelve al
       usuario autenticado. */
    const flag = this.mode === 'preferences'
      ? await this.administratorService.updatePreferences(
          this.neighborhoodId!,
          {
            reportsNotification: this.formAdministrator.value['reportsNotification']
          }
        )
      : await this.administratorService.update(
          this.neighborhoodId!,
          this.administrator!.uidAdministrator,
          this.formAdministrator.value
        );

    if (flag) {
      this.activeModal.close();
    }
  }

}
